import { Bot, User, MapPin } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ChatbotWidgetInitResponse } from "../../services/chatbotWidget.service";

interface Message {
  id: string;
  content: string;
  isFromUser: boolean;
  createdAt: Date;
  type: string;
  sources?: any[];
  mediaUrl?: string;
  workflowData?: any;
  location?: { latitude: number; longitude: number; address?: string };
}

const ChatBubble: React.FC<{ message: Message; config: ChatbotWidgetInitResponse }> = ({ message, config }) => {
  const isUser = message.isFromUser;
  const primaryColor = config?.primaryColor || '#0f766e';
  const secondaryColor = config?.secondaryColor || '#14b8a6';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start max-w-[80%]`}>
        <div
          className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-md overflow-hidden ${isUser ? 'ml-2' : 'mr-2'} ${!isUser ? 'bg-gray-200' : ''}`}
          style={{ backgroundColor: isUser ? primaryColor : undefined }}
        >
          {isUser ? (
            <User size={16} color="white" />
          ) : config.logo ? (
            <img
              src={config.logo}
              alt="Bot logo"
              className="w-6 h-6 object-cover"
            />
          ) : (
            <Bot size={16} className="text-gray-600" />
          )}
        </div>

        <div
          className={`px-3 py-3 shadow-md break-all ${isUser
            ? `rounded-xl rounded-tr-none max-w-full text-white`
            : 'bg-white text-gray-900 rounded-xl rounded-tl-none border border-gray-100'
            }`}
          style={{
            background: isUser ? `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` : undefined,
            borderColor: isUser ? undefined : `${primaryColor}20`
          }}
        >
          {/* Media Messages */}
          {message.type === 'image' && message.mediaUrl && (
            <div className="mb-2">
              <img
                src={message.mediaUrl}
                alt={message.content}
                className="max-w-full rounded-xl"
                style={{ maxHeight: '300px' }}
              />
              {message.content && message.content !== message.mediaUrl && (
                <p className="mt-2 text-sm" style={{ color: isUser ? 'white' : undefined }}>
                  {message.content}
                </p>
              )}
            </div>
          )}

          {message.type === 'video' && message.mediaUrl && (
            <div className="mb-2">
              <video
                src={message.mediaUrl}
                controls
                className="max-w-full rounded-lg"
                style={{ maxHeight: '300px' }}
              />
              {message.content && message.content !== message.mediaUrl && (
                <p className="mt-2 text-sm" style={{ color: isUser ? 'white' : undefined }}>
                  {message.content}
                </p>
              )}
            </div>
          )}

          {message.type === 'audio' && message.mediaUrl && (
            <div className="mb-2">
              <audio
                src={message.mediaUrl}
                controls
                className="max-w-full"
              />
              {message.content && message.content !== message.mediaUrl && (
                <p className="mt-2 text-sm" style={{ color: isUser ? 'white' : undefined }}>
                  {message.content}
                </p>
              )}
            </div>
          )}

          {message.type === 'document' && message.mediaUrl && (
            <div className="mb-2">
              <a
                href={message.mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm hover:underline"
                style={{ color: isUser ? 'white' : '#2563eb' }}
              >
                📎 {message.content || 'Document'}
              </a>
            </div>
          )}

          {message.type === 'location' && message.location && (
            <div className="mb-2 w-full max-w-[250px] overflow-hidden rounded-xl border border-gray-200 bg-white">
              <div className="relative h-32 w-full overflow-hidden bg-gray-100">
                <iframe
                  title="Location Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://maps.google.com/maps?q=${message.location.latitude},${message.location.longitude}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                ></iframe>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${message.location.latitude},${message.location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 z-10"
                ></a>
              </div>
              <div className="p-2 flex items-center gap-2">
                <div className="bg-emerald-100 p-1.5 rounded-full shrink-0">
                  <MapPin size={16} className="text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {message.location.address || 'Shared Location'}
                  </p>
                  <p className="text-[10px] text-gray-500 truncate">
                    {message.location.latitude.toFixed(4)}, {message.location.longitude.toFixed(4)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Text Messages */}
          {(!message.type || message.type === 'text') && (
            <div className={`prose prose-sm max-w-none ${isUser ? 'prose-invert text-white' : 'text-gray-800'}`}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                  li: ({ children }) => <li className="mb-1">{children}</li>,
                  code: ({ children }) => <code className={`px-1 py-0.5 rounded text-xs ${isUser ? 'bg-white/20' : 'bg-gray-100 text-gray-700'}`}>{children}</code>,
                  pre: ({ children }) => <pre className={`p-2 rounded my-2 overflow-x-auto ${isUser ? 'bg-white/10' : 'bg-gray-100'}`}>{children}</pre>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`underline hover:opacity-80 cursor-pointer ${isUser ? 'text-white' : 'text-blue-600'}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {/* Sources */}
          {message.sources && message.sources.length > 0 && (
            <div className={`mt-2 pt-2 border-t ${isUser ? 'border-white/20' : 'border-gray-100'}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1">Sources</p>
              <div className="flex flex-wrap gap-1">
                {message.sources.map((source: any, idx: number) => (
                  <div key={idx} className={`px-1.5 py-0.5 rounded text-[10px] ${isUser ? 'bg-white/10' : 'bg-gray-50 text-gray-500'}`}>
                    {source.title || `Source ${idx + 1}`}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className={`text-[10px] opacity-60 mt-1 text-right ${isUser ? 'text-white' : 'text-gray-400'}`}>
            {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatBubble;
